import React, { useMemo, useState, useEffect } from 'react';
import type {
  BillingClient,
  BusinessInfo,
  ClientInvoice,
  PurchaseOrder,
  PurchasingInvoiceDefaults,
  Supplier,
  SupplierInvoice,
} from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import BillingClientModal from '../components/invoicing/BillingClientModal';
import SupplierInvoiceBuilder from '../components/invoicing/SupplierInvoiceBuilder';
import ClientInvoiceBuilder from '../components/invoicing/ClientInvoiceBuilder';
import { computePoTotals } from '../lib/nzGst';
import { invoiceItemsTotal } from '../lib/invoiceUtils';
import { generateSupplierInvoicePdf } from '../lib/supplierInvoicePdf';
import { generateClientInvoicePdf } from '../lib/clientInvoicePdf';
import { buildClientInvoiceViewModel, enrichClientInvoiceViewModelForPrint, type ClientInvoiceViewModel } from '../lib/clientInvoiceTemplate';
import ClientInvoicePreviewModal from '../components/invoicing/ClientInvoicePreviewModal';

type Tab = 'supplier' | 'client' | 'clients';

interface InvoicesPageProps {
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  billingClients: BillingClient[];
  supplierInvoices: SupplierInvoice[];
  clientInvoices: ClientInvoice[];
  billingClientsLoading?: boolean;
  supplierInvoicesLoading?: boolean;
  clientInvoicesLoading?: boolean;
  businessInfo?: BusinessInfo | null;
  purchasingDefaults?: PurchasingInvoiceDefaults;
  buffetBreakfastPrice?: number;
  currentStaffId?: number;
  prefillSupplierPoId?: string;
  onClearPrefill?: () => void;
  onSaveBillingClient: (client: BillingClient) => void | Promise<void>;
  onDeleteBillingClient: (id: number) => void | Promise<void>;
  onSaveSupplierInvoice: (invoice: SupplierInvoice) => void | Promise<void>;
  onSaveClientInvoice: (invoice: ClientInvoice) => void | Promise<void>;
}

const InvoicesPage: React.FC<InvoicesPageProps> = ({
  suppliers,
  purchaseOrders,
  billingClients,
  supplierInvoices,
  clientInvoices,
  billingClientsLoading = false,
  supplierInvoicesLoading = false,
  clientInvoicesLoading = false,
  businessInfo,
  purchasingDefaults,
  buffetBreakfastPrice,
  currentStaffId,
  prefillSupplierPoId,
  onClearPrefill,
  onSaveBillingClient,
  onDeleteBillingClient,
  onSaveSupplierInvoice,
  onSaveClientInvoice,
}) => {
  const [tab, setTab] = useState<Tab>(prefillSupplierPoId ? 'supplier' : 'client');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<BillingClient | null>(null);
  const [supplierBuilderOpen, setSupplierBuilderOpen] = useState(Boolean(prefillSupplierPoId));
  const [clientBuilderOpen, setClientBuilderOpen] = useState(false);
  const [editingSupplierInv, setEditingSupplierInv] = useState<SupplierInvoice | null>(null);
  const [editingClientInv, setEditingClientInv] = useState<ClientInvoice | null>(null);
  const [prefillPoId, setPrefillPoId] = useState(prefillSupplierPoId);
  const [newInvoiceClientId, setNewInvoiceClientId] = useState<number | undefined>();
  const [previewClientInvoice, setPreviewClientInvoice] = useState<ClientInvoice | null>(null);
  const [clientPreviewVm, setClientPreviewVm] = useState<ClientInvoiceViewModel | null>(null);
  const [clientPdfLoading, setClientPdfLoading] = useState(false);

  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const clientMap = useMemo(() => new Map(billingClients.map((c) => [c.id, c])), [billingClients]);

  const tabLoading =
    tab === 'supplier'
      ? supplierInvoicesLoading
      : tab === 'client'
        ? billingClientsLoading || clientInvoicesLoading
        : billingClientsLoading;

  useEffect(() => {
    if (!previewClientInvoice || !businessInfo) {
      setClientPreviewVm(null);
      return;
    }
    const client = clientMap.get(previewClientInvoice.clientId);
    if (!client) {
      setClientPreviewVm(null);
      return;
    }
    let cancelled = false;
    const baseVm = buildClientInvoiceViewModel({
      invoice: previewClientInvoice,
      client,
      businessInfo,
      defaults: purchasingDefaults,
    });
    void enrichClientInvoiceViewModelForPrint(baseVm).then((enriched) => {
      if (!cancelled) setClientPreviewVm(enriched);
    });
    return () => {
      cancelled = true;
    };
  }, [previewClientInvoice, clientMap, businessInfo, purchasingDefaults]);

  const filteredSupplierInvoices = useMemo(() => {
    if (paymentFilter === 'all') return supplierInvoices;
    return supplierInvoices.filter((i) => i.paymentStatus === paymentFilter);
  }, [supplierInvoices, paymentFilter]);

  const filteredClientInvoices = useMemo(() => {
    if (paymentFilter === 'all') return clientInvoices;
    return clientInvoices.filter((i) => i.paymentStatus === paymentFilter);
  }, [clientInvoices, paymentFilter]);

  const openNewSupplierInvoice = (poId?: string) => {
    setEditingSupplierInv(null);
    setPrefillPoId(poId);
    setSupplierBuilderOpen(true);
  };

  const closeSupplierBuilder = () => {
    setSupplierBuilderOpen(false);
    setEditingSupplierInv(null);
    setPrefillPoId(undefined);
    onClearPrefill?.();
  };

  const openClientInvoicePreview = (inv: ClientInvoice) => {
    const client = clientMap.get(inv.clientId);
    if (!client || !businessInfo) return;
    setPreviewClientInvoice(inv);
  };

  const downloadClientInvoicePdf = async (inv: ClientInvoice) => {
    const client = clientMap.get(inv.clientId);
    if (!client || !businessInfo) return;
    setClientPdfLoading(true);
    try {
      await generateClientInvoicePdf({
        invoice: inv,
        client,
        businessInfo,
        defaults: purchasingDefaults,
      });
    } finally {
      setClientPdfLoading(false);
    }
  };

  const headerAction = () => {
    if (tab === 'clients') {
      setEditingClient(null);
      setClientModalOpen(true);
      return;
    }
    if (tab === 'supplier') openNewSupplierInvoice();
    else {
      setEditingClientInv(null);
      setNewInvoiceClientId(undefined);
      setClientBuilderOpen(true);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="z-10 flex-shrink-0 bg-white shadow-md">
        <div className="mx-auto flex h-16 max-w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
            <p className="text-sm text-gray-500">
              Supplier bills (incoming) and client tax invoices (outgoing)
            </p>
          </div>
          {tab !== 'clients' || billingClients.length > 0 ? (
            <button
              type="button"
              onClick={headerAction}
              className="flex items-center rounded-lg bg-pink-500 px-4 py-2 font-bold text-white hover:bg-pink-600"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              {tab === 'supplier'
                ? 'Record supplier invoice'
                : tab === 'client'
                  ? 'New client invoice'
                  : 'Add client'}
            </button>
          ) : null}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <nav className="-mb-px mb-4 flex space-x-6 border-b border-gray-200">
          {(
            [
              ['supplier', `Supplier invoices (${supplierInvoices.length})`],
              ['client', `Client invoices (${clientInvoices.length})`],
              ['clients', `Billing clients (${billingClients.length})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`border-b-2 px-1 py-3 text-sm font-medium ${
                tab === key
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab !== 'clients' ? (
          <div className="mb-4 flex gap-2">
            {(['all', 'unpaid', 'paid'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setPaymentFilter(f)}
                className={`rounded-full px-3 py-1 text-sm capitalize ${
                  paymentFilter === f ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        ) : null}

        {!businessInfo?.businessName ? (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Complete <strong>Business Information</strong> in Settings before issuing tax invoices.
          </p>
        ) : null}

        {tabLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-500">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-pink-200 border-t-pink-600" />
            <p className="text-sm">Loading invoices…</p>
          </div>
        ) : null}

        {!tabLoading && tab === 'supplier' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredSupplierInvoices.map((inv) => {
              const supplier = supplierMap.get(inv.supplierId);
              const total = invoiceItemsTotal(inv.items, inv.gstMode);
              return (
                <article key={inv.id} className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="font-semibold">{inv.invoiceNumber}</p>
                  <p className="text-sm text-gray-600">{supplier?.name ?? 'Unknown supplier'}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(inv.invoiceDate).toLocaleDateString()}
                    {inv.purchaseOrderId ? ` · PO ${inv.purchaseOrderId}` : ''}
                  </p>
                  <p className="mt-1 text-lg font-bold text-pink-700">${total.toFixed(2)}</p>
                  <p className="text-xs capitalize text-gray-500">{inv.paymentStatus}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSupplierInv(inv);
                        setSupplierBuilderOpen(true);
                      }}
                      className="text-sm text-pink-600"
                    >
                      Edit
                    </button>
                    {supplier && businessInfo ? (
                      <button
                        type="button"
                        onClick={() =>
                          generateSupplierInvoicePdf({ invoice: inv, supplier, businessInfo })
                        }
                        className="text-sm text-gray-700"
                      >
                        PDF
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
            {filteredSupplierInvoices.length === 0 ? (
              <p className="col-span-full text-center text-gray-500">
                Record supplier tax invoices when bills arrive — link to a PO or enter manually.
              </p>
            ) : null}
          </div>
        ) : null}

        {!tabLoading && tab === 'client' ? (
          billingClients.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-pink-200 bg-pink-50/50 p-8 text-center">
              <p className="font-medium">Add a billing client first</p>
              <p className="mt-1 text-sm text-gray-600">
                e.g. the hotel or company you invoice for breakfast covers.
              </p>
              <button
                type="button"
                onClick={() => {
                  setTab('clients');
                  setEditingClient(null);
                  setClientModalOpen(true);
                }}
                className="mt-4 rounded-lg bg-pink-600 px-4 py-2 text-sm text-white"
              >
                Add billing client
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredClientInvoices.map((inv) => {
                const client = clientMap.get(inv.clientId);
                const total = invoiceItemsTotal(inv.items, inv.gstMode);
                return (
                  <article key={inv.id} className="rounded-xl border bg-white p-4 shadow-sm">
                    <p className="font-semibold">{inv.invoiceNumber}</p>
                    <p className="text-sm text-gray-600">{client?.name ?? 'Unknown client'}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(inv.invoiceDate).toLocaleDateString()}
                    </p>
                    <p className="mt-1 text-lg font-bold text-pink-700">${total.toFixed(2)}</p>
                    <p className="text-xs capitalize text-gray-500">{inv.paymentStatus}</p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingClientInv(inv);
                          setClientBuilderOpen(true);
                        }}
                        className="text-sm text-pink-600"
                      >
                        Edit
                      </button>
                      {client && businessInfo ? (
                        <>
                          <button
                            type="button"
                            onClick={() => openClientInvoicePreview(inv)}
                            className="text-sm text-gray-700"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            disabled={clientPdfLoading}
                            onClick={() => void downloadClientInvoicePdf(inv)}
                            className="text-sm text-gray-700 disabled:opacity-50"
                          >
                            PDF
                          </button>
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
              {filteredClientInvoices.length === 0 ? (
                <p className="col-span-full text-center text-gray-500">
                  Create tax invoices for breakfast or other services billed to your clients.
                </p>
              ) : null}
            </div>
          )
        ) : null}

        {!tabLoading && tab === 'clients' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {billingClients.map((c) => (
              <article key={c.id} className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="font-semibold">{c.name}</h3>
                {c.contactPerson ? <p className="text-sm text-gray-600">{c.contactPerson}</p> : null}
                {c.address ? <p className="text-sm text-gray-500">{c.address}</p> : null}
                {c.defaultBreakfastCoverPrice != null ? (
                  <p className="mt-1 text-sm text-pink-700">
                    Breakfast: ${c.defaultBreakfastCoverPrice.toFixed(2)} / cover
                  </p>
                ) : null}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingClientInv(null);
                      setNewInvoiceClientId(c.id);
                      setClientBuilderOpen(true);
                    }}
                    className="rounded-md bg-pink-600 px-3 py-1 text-sm text-white"
                  >
                    New invoice
                  </button>
                  <button type="button" onClick={() => { setEditingClient(c); setClientModalOpen(true); }}>
                    <EditIcon className="h-5 w-5 text-pink-600" />
                  </button>
                  <button type="button" onClick={() => void onDeleteBillingClient(c.id)}>
                    <TrashIcon className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </main>

      {clientModalOpen ? (
        <BillingClientModal
          client={editingClient}
          onClose={() => setClientModalOpen(false)}
          onSave={async (c) => {
            await onSaveBillingClient(c);
            setClientModalOpen(false);
          }}
        />
      ) : null}

      {supplierBuilderOpen ? (
        <SupplierInvoiceBuilder
          invoice={editingSupplierInv}
          suppliers={suppliers}
          purchaseOrders={purchaseOrders}
          prefillPurchaseOrderId={prefillPoId}
          businessInfo={businessInfo}
          purchasingDefaults={purchasingDefaults}
          staffId={currentStaffId}
          onClose={closeSupplierBuilder}
          onSave={async (inv) => {
            await onSaveSupplierInvoice(inv);
            closeSupplierBuilder();
          }}
        />
      ) : null}

      {clientBuilderOpen ? (
        <ClientInvoiceBuilder
          invoice={editingClientInv}
          billingClients={billingClients}
          initialClientId={newInvoiceClientId}
          businessInfo={businessInfo}
          purchasingDefaults={purchasingDefaults}
          buffetBreakfastPrice={buffetBreakfastPrice}
          staffId={currentStaffId}
          onClose={() => {
            setClientBuilderOpen(false);
            setEditingClientInv(null);
            setNewInvoiceClientId(undefined);
          }}
          onSave={async (inv) => {
            await onSaveClientInvoice(inv);
            setClientBuilderOpen(false);
            setEditingClientInv(null);
          }}
        />
      ) : null}

      {clientPreviewVm && previewClientInvoice ? (
        <ClientInvoicePreviewModal
          viewModel={clientPreviewVm}
          onClose={() => setPreviewClientInvoice(null)}
          downloading={clientPdfLoading}
          onDownloadPdf={() => void downloadClientInvoicePdf(previewClientInvoice)}
        />
      ) : null}
    </div>
  );
};

export default InvoicesPage;
